<aside
  *ngIf="row"
  id="wo-order-panel"
  class="wo-panel fixed bottom-0 right-0 top-16 z-[80] flex w-full flex-col overflow-hidden border-l border-[var(--wo2-border-strong)] bg-[var(--wo2-surface)] text-[var(--wo2-text)] sm:w-[620px]"
  role="dialog"
  aria-modal="false"
  aria-labelledby="wo-panel-title"
  [attr.data-theme]="themeMode"
  [attr.aria-busy]="loading || saving"
>
  <header class="wo-panel-header shrink-0 border-b border-[var(--wo2-border)] px-6 pb-5 pt-5">
    <div class="flex items-start justify-between gap-4">
      <div class="min-w-0">
        <div
          class="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[var(--wo2-muted)]"
        >
          <i class="fa-solid fa-truck-fast text-[var(--wo2-primary)]" aria-hidden="true"></i>
          Work order
        </div>
        <h2 id="wo-panel-title" class="m-0 break-words text-2xl font-bold tracking-tight">
          {{ orderLabel }}
        </h2>
        <p class="m-0 mt-1.5 text-sm text-[var(--wo2-muted)]">
          {{ details?.lading?.customer_name || row.customer_name || 'Customer not set' }}
        </p>
      </div>
      <button
        #closeButton
        type="button"
        class="wo-panel-icon-button shrink-0"
        aria-label="Close work order panel"
        [disabled]="saving"
        (click)="close()"
      >
        <i class="fa-solid fa-xmark text-lg" aria-hidden="true"></i>
      </button>
    </div>

    <div class="mt-5 flex flex-wrap items-center justify-between gap-3">
      <span
        class="wo-panel-status inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold"
      >
        <span class="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true"></span>
        {{ currentStatus }}
      </span>
      <button type="button" class="wo-panel-secondary" [disabled]="saving" (click)="openFullDetails()">
        <span>Open Full Details</span>
        <i class="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i>
      </button>
    </div>

    <div *ngIf="routeLabel" class="mt-4 flex items-start gap-2 text-xs leading-5 text-[var(--wo2-muted)]">
      <i class="fa-solid fa-route mt-1 text-[var(--wo2-primary)]" aria-hidden="true"></i>
      <span>{{ routeLabel }}</span>
    </div>
  </header>

  <nav class="wo-panel-navigation" aria-label="Work order sections">
    <button
      *ngFor="let section of sections"
      type="button"
      class="wo-panel-tab"
      [class.is-active]="activeTab === section.id"
      [attr.aria-pressed]="activeTab === section.id"
      (click)="selectTab(section.id)"
    >
      <i class="fa-solid" [ngClass]="section.icon" aria-hidden="true"></i>
      <span>{{ section.label }}</span>
      <span
        *ngIf="sectionHasDraft(section.id)"
        class="wo-panel-draft-dot"
        aria-label="Unsaved changes"
      ></span>
    </button>
  </nav>

  <div
    #panelBody
    tabindex="-1"
    aria-label="Work order section"
    class="wo-panel-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5"
  >
    <div
      *ngIf="loading"
      class="flex min-h-56 flex-col items-center justify-center gap-4 text-sm text-[var(--wo2-muted)]"
      role="status"
    >
      <span
        class="h-8 w-8 animate-spin rounded-full border-2 border-[var(--wo2-border)] border-t-sky-400"
      ></span>
      Loading work order…
    </div>

    <div *ngIf="loadError && !loading" class="wo-panel-notice" data-tone="error" role="alert">
      <p class="m-0">{{ loadError }}</p>
      <button type="button" class="wo-panel-secondary mt-4" (click)="loadOrder()">Try again</button>
    </div>

    <div
      *ngIf="notice"
      class="wo-panel-notice mb-5"
      [attr.data-tone]="notice.tone"
      [attr.role]="notice.tone === 'error' ? 'alert' : 'status'"
    >
      <i
        class="fa-solid"
        [ngClass]="notice.tone === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'"
        aria-hidden="true"
      ></i>
      <span>{{ notice.message }}</span>
    </div>

    <ng-container *ngIf="details && !loading && !loadError">
      <div [hidden]="activeTab !== 'overview'" class="space-y-6">
        <app-purchase-order-editor [ladingUuid]="row.lading_uuid" [disabled]="saving" (saved)="onPurchaseOrdersSaved()"></app-purchase-order-editor>
        <form [formGroup]="referenceForm" (ngSubmit)="saveReferences()" class="wo-panel-section">
          <div class="mb-5 flex items-start gap-3">
            <span class="wo-panel-section-icon"><i class="fa-solid fa-hashtag" aria-hidden="true"></i></span>
            <div>
              <h3 class="m-0 text-base font-semibold">Order references</h3>
            </div>
          </div>
          <fieldset [disabled]="saving" class="m-0 min-w-0 border-0 p-0">
            <div class="grid grid-cols-1 gap-4 min-[420px]:grid-cols-2">

              <label class="wo-panel-label">
                <span>Pickup reference</span>
                <input
                  class="wo-panel-input"
                  data-panel-field="pickup_reference_number"
                  formControlName="pickup_reference_number"
                  autocomplete="off"
                />
              </label>
              <label class="wo-panel-label">
                <span>Delivery reference</span>
                <input
                  class="wo-panel-input"
                  data-panel-field="delivery_reference_number"
                  formControlName="delivery_reference_number"
                  autocomplete="off"
                />
              </label>
              <label class="wo-panel-label">
                <span>Booking number</span>
                <input
                  class="wo-panel-input"
                  data-panel-field="booking_number"
                  formControlName="booking_number"
                  autocomplete="off"
                />
              </label>
              <label class="wo-panel-label">
                <span>Release number</span>
                <input
                  class="wo-panel-input"
                  data-panel-field="release_number"
                  formControlName="release_number"
                  autocomplete="off"
                />
              </label>
            </div>
            <div class="mt-5 flex items-center justify-end">
              <button type="submit" class="wo-panel-primary" [disabled]="saving || referenceForm.pristine">
                <i
                  class="fa-solid"
                  [ngClass]="savingSection === 'references' ? 'fa-spinner fa-spin' : 'fa-check'"
                  aria-hidden="true"
                ></i>
                {{ savingSection === 'references' ? 'Saving…' : 'Save references' }}
              </button>
            </div>
          </fieldset>
        </form>
      </div>

      <div [hidden]="activeTab !== 'route'" class="space-y-5">
        <app-work-order-stop-editor
          *ngFor="let role of stopRoles; let index = index"
          #stopEditor
          [details]="details"
          [role]="role"
          [busy]="saving"
          (savingChanged)="onStopSaving(role, $event)"
          (saved)="onStopSaved($event, role)"
        >
          <ng-container
            *ngTemplateOutlet="contactCard; context: { $implicit: routeContacts[index], editor: stopEditor }"
          ></ng-container>
        </app-work-order-stop-editor>
      </div>

      <div [hidden]="activeTab !== 'contacts'" class="space-y-5">
        <ng-container *ngFor="let contact of partyContacts">
          <ng-container *ngTemplateOutlet="contactCard; context: { $implicit: contact }"></ng-container>
        </ng-container>
      </div>

      <div [hidden]="activeTab !== 'schedule'" class="space-y-5">
        <form [formGroup]="appointmentForm" (ngSubmit)="saveAppointment()" class="wo-panel-section">
          <div class="mb-5 flex items-start gap-3">
            <span class="wo-panel-section-icon">
              <i class="fa-regular fa-calendar-check" aria-hidden="true"></i>
            </span>
            <div>
              <h3 class="m-0 text-base font-semibold">Appointment window</h3>
              <p class="m-0 mt-1 text-xs leading-5 text-[var(--wo2-muted)]">
                Use the appointment location’s local time.
              </p>
            </div>
          </div>
          <fieldset [disabled]="saving" class="m-0 min-w-0 border-0 p-0">
            <div class="grid grid-cols-1 gap-4 min-[420px]:grid-cols-2">
              <label class="wo-panel-label">
                <span>Appointment date</span>
                <input
                  type="date"
                  class="wo-panel-input"
                  data-panel-field="appointment_date"
                  formControlName="date"
                  (change)="onAppointmentDateChange()"
                />
              </label>
              <label class="wo-panel-label">
                <span>Start time</span>
                <input
                  type="time"
                  class="wo-panel-input"
                  data-panel-field="appointment_start"
                  formControlName="start"
                />
              </label>
              <label class="wo-panel-label">
                <span>End date</span>
                <input type="date" class="wo-panel-input" formControlName="endDate" />
              </label>
              <label class="wo-panel-label">
                <span>End time</span>
                <input
                  type="time"
                  class="wo-panel-input"
                  data-panel-field="appointment_end"
                  formControlName="end"
                />
              </label>
            </div>
            <label class="wo-panel-label mt-4">
              <span>Timezone</span>
              <input
                class="wo-panel-input"
                data-panel-field="appointment_timezone"
                formControlName="timezone"
                list="wo-panel-timezones"
                placeholder="America/Toronto"
                autocomplete="off"
              />
            </label>
            <p
              *ngIf="appointmentForm.dirty && appointmentError"
              class="mt-3 text-sm text-rose-500"
              role="alert"
            >
              {{ appointmentError }}
            </p>
            <div class="mt-5 flex flex-wrap items-center justify-between gap-3">
              <button type="button" class="wo-panel-text-button" (click)="clearAppointment()">
                Clear appointment
              </button>
              <button
                type="submit"
                class="wo-panel-primary"
                [disabled]="saving || appointmentForm.pristine || !!appointmentError"
              >
                <i
                  class="fa-solid"
                  [ngClass]="savingSection === 'appointment' ? 'fa-spinner fa-spin' : 'fa-check'"
                  aria-hidden="true"
                ></i>
                {{ savingSection === 'appointment' ? 'Saving…' : 'Save appointment' }}
              </button>
            </div>
          </fieldset>
        </form>
        <section class="wo-panel-section" data-panel-field="planned_times" tabindex="-1">
          <div class="mb-4 flex items-center justify-between gap-3">
            <h3 class="m-0 text-base font-semibold">Pickup &amp; delivery schedule</h3>
            <button
              type="button"
              class="wo-panel-text-button"
              [disabled]="saving"
              (click)="openFullDetails('general-info')"
            >
              Full Details
            </button>
          </div>
          <dl class="m-0 space-y-4">
            <div *ngFor="let fact of plannedTimes">
              <dt class="text-xs font-semibold text-[var(--wo2-muted)]">{{ fact.label }}</dt>
              <dd class="m-0 mt-1 text-sm leading-6">{{ fact.value }}</dd>
            </div>
          </dl>
        </section>
      </div>

      <div [hidden]="activeTab !== 'freight'" class="space-y-5">
        <section class="wo-panel-section" data-panel-field="freight_summary" tabindex="-1">
          <div class="mb-5 flex items-center justify-between gap-3">
            <h3 class="m-0 text-base font-semibold">Freight summary</h3>
            <button
              type="button"
              class="wo-panel-text-button"
              [disabled]="saving"
              (click)="openFullDetails('freight')"
            >
              Packages &amp; products
            </button>
          </div>
          <dl class="m-0 grid grid-cols-2 gap-x-5 gap-y-4 min-[420px]:grid-cols-3">
            <div *ngFor="let fact of freightSummary">
              <dt class="text-xs font-semibold text-[var(--wo2-muted)]">{{ fact.label }}</dt>
              <dd class="m-0 mt-1 break-words text-base font-semibold">{{ fact.value }}</dd>
            </div>
          </dl>
        </section>

        <form [formGroup]="freightForm" (ngSubmit)="saveFreight()" class="wo-panel-section">
          <h3 class="m-0 mb-5 text-base font-semibold">Equipment &amp; instructions</h3>
          <fieldset [disabled]="saving" class="m-0 min-w-0 border-0 p-0">
            <label class="wo-panel-label">
              <span>Equipment type</span>
              <select
                class="wo-panel-input"
                formControlName="type_of_equipment"
                data-panel-field="type_of_equipment"
              >
                <option value="">Not set</option>
                <option *ngIf="hasUnknownEquipment" [value]="freightForm.controls.type_of_equipment.value">
                  {{ freightForm.controls.type_of_equipment.value }}
                </option>
                <option *ngFor="let equipment of equipmentTypes" [value]="equipment.code">
                  {{ equipment.name }}
                </option>
              </select>
            </label>
            <div class="mt-4 grid grid-cols-1 gap-4 min-[420px]:grid-cols-2">
              <label class="wo-panel-label">
                <span>Trailer number</span>
                <input
                  class="wo-panel-input"
                  formControlName="carrier_trailer"
                  data-panel-field="carrier_trailer"
                  autocomplete="off"
                />
              </label>
              <label class="wo-panel-label">
                <span>Tractor number</span>
                <input
                  class="wo-panel-input"
                  formControlName="carrier_tractor"
                  data-panel-field="carrier_tractor"
                  autocomplete="off"
                />
              </label>
              <label class="wo-panel-label">
                <span>Seal number</span>
                <input
                  class="wo-panel-input"
                  formControlName="seal_number"
                  data-panel-field="seal_number"
                  autocomplete="off"
                />
              </label>
            </div>
            <label class="wo-panel-label mt-4">
              <span>Load description</span>
              <textarea
                class="wo-panel-input resize-y leading-6"
                rows="3"
                formControlName="load_details"
                data-panel-field="load_details"
              ></textarea>
            </label>
            <label class="wo-panel-label mt-4">
              <span>Special instructions</span>
              <textarea
                class="wo-panel-input resize-y leading-6"
                rows="4"
                formControlName="load_special_info"
                data-panel-field="load_special_info"
              ></textarea>
            </label>
            <div class="mt-5 flex justify-end">
              <button type="submit" class="wo-panel-primary" [disabled]="saving || freightForm.pristine">
                <i
                  class="fa-solid"
                  [ngClass]="savingSection === 'freight' ? 'fa-spinner fa-spin' : 'fa-check'"
                  aria-hidden="true"
                ></i>
                {{ savingSection === 'freight' ? 'Saving…' : 'Save freight & equipment' }}
              </button>
            </div>
          </fieldset>
        </form>
      </div>

      <div [hidden]="activeTab !== 'notes'" class="space-y-5">
        <section class="wo-panel-section">
          <div class="mb-4 flex items-center justify-between gap-3">
            <h3 class="m-0 text-base font-semibold">{{ editingNote ? 'Edit note' : 'Add a note' }}</h3>
            <button
              *ngIf="editingNote"
              type="button"
              class="wo-panel-text-button"
              [disabled]="saving"
              (click)="editNote()"
            >
              Cancel edit
            </button>
          </div>
          <label class="wo-panel-label">
            <span class="sr-only">Work order note</span>
            <textarea
              class="wo-panel-input min-h-[144px] resize-y leading-6"
              rows="5"
              [(ngModel)]="noteDraft"
              [disabled]="saving"
              placeholder="Write a note…"
            ></textarea>
          </label>
          <div class="mt-4 flex justify-end">
            <button
              type="button"
              class="wo-panel-primary"
              [disabled]="saving || !canSaveNote"
              (click)="saveNote()"
            >
              <i
                class="fa-solid"
                [ngClass]="savingSection === 'notes' ? 'fa-spinner fa-spin' : 'fa-check'"
                aria-hidden="true"
              ></i>
              {{ savingSection === 'notes' ? 'Saving…' : 'Save note' }}
            </button>
          </div>
        </section>
        <div class="flex items-center justify-between text-xs font-semibold text-[var(--wo2-muted)]">
          <span>NOTE HISTORY</span>
          <span>{{ notes.length }} {{ notes.length === 1 ? 'note' : 'notes' }}</span>
        </div>
        <p *ngIf="notes.length === 0" class="py-5 text-center text-sm text-[var(--wo2-muted)]">
          No notes yet.
        </p>
        <article *ngFor="let note of notes" class="wo-panel-section">
          <div class="mb-3 flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="m-0 break-words text-xs font-semibold">
                {{ note.modified_by || note.created_by || 'Work order note' }}
              </p>
              <p class="m-0 mt-1 text-xs text-[var(--wo2-muted)]">
                {{ formatTimestamp(note.date_last_modified || note.date) }}
              </p>
            </div>
            <button
              type="button"
              class="wo-panel-icon-button shrink-0"
              aria-label="Edit note"
              [disabled]="saving"
              (click)="editNote(note)"
            >
              <i class="fa-solid fa-pen text-xs" aria-hidden="true"></i>
            </button>
          </div>
          <p class="m-0 whitespace-pre-wrap break-words text-sm leading-6">{{ note.note }}</p>
        </article>
      </div>

      <div [hidden]="activeTab !== 'status'" class="space-y-5">
        <form [formGroup]="statusForm" (ngSubmit)="saveStatus()" class="wo-panel-section">
          <div class="mb-5 flex items-start gap-3">
            <span class="wo-panel-section-icon"><i class="fa-solid fa-route" aria-hidden="true"></i></span>
            <div>
              <h3 class="m-0 text-base font-semibold">Update status</h3>
            </div>
          </div>
          <fieldset [disabled]="saving" class="m-0 min-w-0 border-0 p-0">
            <label class="wo-panel-label">
              <span>New status</span>
              <select class="wo-panel-input" formControlName="status">
                <option value="">Choose a status</option>
                <option *ngFor="let status of statuses" [value]="status">{{ status }}</option>
              </select>
            </label>
            <div class="mt-4 grid grid-cols-1 gap-4 min-[420px]:grid-cols-2">
              <label class="wo-panel-label">
                <span>Event date</span>
                <input class="wo-panel-input" type="date" formControlName="date" />
              </label>
              <label class="wo-panel-label">
                <span>Event time</span>
                <input class="wo-panel-input" type="time" formControlName="time" />
              </label>
            </div>
            <label class="wo-panel-label mt-4">
              <span>Event timezone</span>
              <input
                class="wo-panel-input"
                formControlName="timezone"
                list="wo-panel-timezones"
                autocomplete="off"
              />
            </label>
            <label class="wo-panel-label mt-4">
              Status note
              <span class="font-normal text-[var(--wo2-muted)]">(optional)</span>
              <textarea class="wo-panel-input resize-y" rows="3" formControlName="note"></textarea>
            </label>
            <p *ngIf="statusForm.dirty && statusError" class="mt-3 text-sm text-rose-500" role="alert">
              {{ statusError }}
            </p>
            <div class="mt-5 flex justify-end">
              <button type="submit" class="wo-panel-primary" [disabled]="saving || !!statusError">
                <i
                  class="fa-solid"
                  [ngClass]="savingSection === 'status' ? 'fa-spinner fa-spin' : 'fa-check'"
                  aria-hidden="true"
                ></i>
                {{ savingSection === 'status' ? 'Saving…' : 'Save status' }}
              </button>
            </div>
          </fieldset>
        </form>
        <h3 class="m-0 text-xs font-semibold uppercase tracking-wider text-[var(--wo2-muted)]">
          Recent status history
        </h3>
        <p *ngIf="statusHistory.length === 0" class="py-5 text-center text-sm text-[var(--wo2-muted)]">
          No status history yet.
        </p>
        <ol class="m-0 list-none space-y-0 p-0">
          <li
            *ngFor="let event of statusHistory | slice: 0 : 8"
            class="wo-panel-timeline-item relative ml-2 border-l border-[var(--wo2-border-strong)] pb-6 pl-6"
          >
            <span
              class="absolute -left-1.5 top-1 h-3 w-3 rounded-full border-2 border-[var(--wo2-surface)] bg-[var(--wo2-primary)]"
              aria-hidden="true"
            ></span>
            <p class="m-0 text-sm font-semibold">{{ event.lading_status }}</p>
            <p class="m-0 mt-1 text-xs text-[var(--wo2-muted)]">
              {{ formatTimestamp(event.local_time) }} {{ event.time_zone_id }}
            </p>
            <p
              *ngIf="event.lading_status_note"
              class="m-0 mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-[var(--wo2-muted)]"
            >
              {{ event.lading_status_note }}
            </p>
          </li>
        </ol>
      </div>
    </ng-container>
  </div>

  <footer
    class="flex shrink-0 items-center justify-between gap-3 border-t border-[var(--wo2-border)] bg-[var(--wo2-surface-2)] px-6 py-3.5"
  >
    <span class="flex items-center gap-2 text-xs text-[var(--wo2-muted)]" aria-live="polite">
      <span *ngIf="hasUnsavedChanges" class="wo-panel-draft-dot" aria-hidden="true"></span>
      {{ saving ? 'Saving changes…' : hasUnsavedChanges ? 'Unsaved changes' : '' }}
    </span>
    <button type="button" class="wo-panel-secondary" [disabled]="saving" (click)="close()">
      Close panel
    </button>
  </footer>

  <ng-template #contactCard let-contact let-editor="editor">
    <section class="wo-panel-section" [attr.data-panel-field]="contact.role" tabindex="-1">
      <div class="mb-5 flex items-start gap-3">
        <span class="wo-panel-section-icon">
          <i
            class="fa-solid"
            [ngClass]="
              contact.role === 'shipper' || contact.role === 'consignee'
                ? 'fa-location-dot'
                : 'fa-address-book'
            "
            aria-hidden="true"
          ></i>
        </span>
        <div class="min-w-0">
          <p class="m-0 mb-1 text-xs font-semibold uppercase tracking-wider text-[var(--wo2-primary)]">
            {{ contact.subtitle }}
          </p>
          <h3 class="m-0 break-words text-base font-semibold leading-6">{{ contact.name }}</h3>
          <p *ngIf="contact.person" class="m-0 mt-1 text-sm text-[var(--wo2-muted)]">{{ contact.person }}</p>
        </div>
      </div>
      <div class="space-y-4">
        <div *ngFor="let address of contact.addresses; let index = index">
          <p
            *ngIf="contact.addresses.length > 1"
            class="m-0 mb-1 text-xs font-semibold text-[var(--wo2-muted)]"
          >
            {{ address.type || 'Address ' + (index + 1) }}
          </p>
          <address class="text-sm not-italic leading-6">
            <div *ngFor="let line of address.lines">{{ line }}</div>
          </address>
          <p *ngIf="address.timezone" class="m-0 mt-2 text-xs text-[var(--wo2-muted)]">
            {{ address.timezone }}
          </p>
        </div>
        <p *ngIf="contact.addresses.length === 0" class="m-0 text-sm text-[var(--wo2-muted)]">
          {{ contact.addressMessage }}
        </p>
        <div
          *ngFor="let phone of contact.phones"
          class="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 text-sm"
        >
          <div class="min-w-0">
            <p *ngIf="phone.name" class="m-0 font-medium">{{ phone.name }}</p>
            <p *ngIf="phone.description" class="m-0 text-xs text-[var(--wo2-muted)]">
              {{ phone.description }}
            </p>
          </div>
          <a class="wo-panel-contact-link" [href]="phone.href">{{ phone.number }}</a>
        </div>
        <a
          *ngIf="contact.email"
          class="wo-panel-contact-link block break-all text-sm"
          [href]="'mailto:' + contact.email"
        >
          {{ contact.email }}
        </a>
        <dl
          *ngIf="contact.reference || contact.hours"
          class="m-0 grid grid-cols-1 gap-3 min-[420px]:grid-cols-2"
        >
          <div *ngIf="contact.reference">
            <dt class="text-xs font-semibold text-[var(--wo2-muted)]">{{ contact.subtitle }} reference</dt>
            <dd class="m-0 mt-1 break-words text-sm">{{ contact.reference }}</dd>
          </div>
          <div *ngIf="contact.hours">
            <dt class="text-xs font-semibold text-[var(--wo2-muted)]">{{ contact.subtitle }} hours</dt>
            <dd class="m-0 mt-1 text-sm">{{ contact.hours }}</dd>
          </div>
        </dl>
      </div>
      <div class="mt-5 flex flex-wrap justify-end gap-3">
        <button
          *ngIf="editor"
          type="button"
          class="wo-panel-primary"
          [disabled]="saving"
          (click)="editor.startEdit()"
        >
          <i class="fa-solid fa-pen" aria-hidden="true"></i>
          Edit {{ contact.title | lowercase }}
        </button>
        <button
          type="button"
          class="wo-panel-secondary"
          [disabled]="saving"
          (click)="openFullDetails(contact.role + '-info')"
        >
          {{ contact.title }} details
          <i class="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i>
        </button>
      </div>
    </section>
  </ng-template>

  <datalist id="wo-panel-timezones">
    <option value="America/Toronto"></option>
    <option value="America/New_York"></option>
    <option value="America/Chicago"></option>
    <option value="America/Winnipeg"></option>
    <option value="America/Regina"></option>
    <option value="America/Edmonton"></option>
    <option value="America/Denver"></option>
    <option value="America/Vancouver"></option>
    <option value="America/Los_Angeles"></option>
    <option value="America/Halifax"></option>
    <option value="America/St_Johns"></option>
    <option value="UTC"></option>
  </datalist>
</aside>
